/**
 * Sets a value in localStorage with the specified key.
 *
 * @param {string} key - The key to store the value under.
 * @param {*} value - The value to store in localStorage.
 * @throws {TypeError} If the key is not a string.
 */
export const setInLocalStorage = (key, value) => {
  try {
    if (typeof key !== "string") {
      throw new TypeError(`localStorage: Key must be a string. (reading '${key}')`);
    }

    if (typeof value === "object" || Array.isArray(value)) {
      value = JSON.stringify(value);
    }

    localStorage.setItem(key, value);
  } catch (error) {
    console.error("Error setting value in localStorage:", error);
  }
};

/**
 * Retrieves a value from localStorage with the specified key.
 *
 * @param {string} key - The key to retrieve the value from.
 * @param {*} [defaultValue=null] - The default value to return if the key is not found.
 * @returns {*} The value retrieved from localStorage or the default value if the key is not found.
 */
export const getFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);

    if (item === null) {
      return defaultValue;
    }

    if (typeof item !== "string") {
      return item;
    }

    switch (item) {
      case "undefined":
        return undefined;
      case "null":
        return null;
      case "true":
        return true;
      case "false":
        return false;
      default:
        if (/^-?\d+(\.\d+)?$/.test(item)) {
          return Number(item);
        }

        if (/^-?\d+(\.\d+)?e[+-]?\d+$/i.test(item)) {
          return Number(item);
        }

        if (item[0] === "{" || item[0] === "[") {
          return JSON.parse(item);
        }

        return item;
    }
  } catch (error) {
    console.error("Error getting value from localStorage:", error);
    return defaultValue;
  }
};

/**
 * Removes a key-value pair from localStorage.
 * @param {string} key - The key of the value to remove.
 */
export const removeFromLocalStorage = (key) => {
  try {
    if (!key) {
      throw new Error("Key is required");
    }
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Error removing value from localStorage:", error);
  }
};

/**
 * Clears all key-value pairs from localStorage.
 */
export const clearLocalStorage = () => {
  try {
    localStorage.clear();
  } catch (error) {
    console.error("Error clearing localStorage:", error);
  }
};
